// ======================================================================
// FILE: src/modules/wms/team/pages/TeamListPage.jsx
// MODULE: Work Management System (WMS)
// FEATURE: Team Management
// PURPOSE:
//   Team Listing Screen
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import wmsTeamService
from "../../services/wmsTeamService";

export default function TeamListPage() {

  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [pagination, setPagination] =
    useState({});

  const [filters, setFilters] =
    useState({
      q: "",
      completion_mode: "",
      page: 1,
      per_page: 20,
    });

  // ==========================================================
  // LOAD DATA
  // ==========================================================

  async function loadData() {

    try {

      setLoading(true);

      const result =
        await wmsTeamService.listTeams(
          filters
        );

      setRows(
        result.items || []
      );

      setPagination(
        result.pagination || {}
      );

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load teams"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [filters.page]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  function handleSearch() {

    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));

    loadData();
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Team ${row.team_name}?`
    );

    if (!ok) return;

    try {

      await wmsTeamService.deleteTeam(
        row.id
      );

      await loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        className="d-flex justify-content-between align-items-center mb-3"
      >
        <h3>
          Team Management
        </h3>

        <div>

          <button
            className="btn btn-secondary me-2"
            onClick={loadData}
          >
            Refresh
          </button>

          <Link
            to="/wms/teams/create"
            className="btn btn-primary"
          >
            Create Team
          </Link>

        </div>

      </div>

      {/* ====================================================== */}
      {/* FILTERS */}
      {/* ====================================================== */}

      <div className="card mb-3">

        <div className="card-body">

          <div className="row">

            <div className="col-md-4">

              <label>
                Search
              </label>

              <input
                className="form-control"
                value={filters.q}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    q: e.target.value,
                  })
                }
                placeholder="Code / Name"
              />

            </div>

            <div className="col-md-3">

              <label>
                Completion Mode
              </label>

              <select
                className="form-control"
                value={
                  filters.completion_mode
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    completion_mode:
                      e.target.value,
                  })
                }
              >
                <option value="">
                  All
                </option>

                <option value="ANY_MEMBER">
                  Any Member
                </option>

                <option value="ALL_MEMBERS">
                  All Members
                </option>

                <option value="PERCENTAGE">
                  Percentage
                </option>

              </select>

            </div>

            <div
              className="col-md-2 d-flex align-items-end"
            >

              <button
                className="btn btn-primary"
                onClick={handleSearch}
              >
                Search
              </button>

            </div>

          </div>

        </div>

      </div>

      {/* ====================================================== */}
      {/* GRID */}
      {/* ====================================================== */}

      <div className="card">

        <div className="card-body p-0">

          <table
            className="table table-bordered table-hover mb-0"
          >

            <thead>

              <tr>

                <th>
                  Team Code
                </th>

                <th>
                  Team Name
                </th>

                <th>
                  Department
                </th>

                <th>
                  Completion Mode
                </th>

                <th>
                  Team Lead
                </th>

                <th>
                  Status
                </th>

                <th width="220">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    Loading...
                  </td>

                </tr>
              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    No Records Found
                  </td>

                </tr>
              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.team_code}
                  </td>

                  <td>
                    {row.team_name}
                  </td>

                  <td>
                    {row.department_id}
                  </td>

                  <td>
                    {row.completion_mode}
                  </td>

                  <td>
                    {row.team_lead_user_id}
                  </td>

                  <td>

                    {row.is_active
                      ? "Active"
                      : "Inactive"}

                  </td>

                  <td>

                    <button
                      className="btn btn-sm btn-info me-1"
                      onClick={() =>
                        navigate(
                          `/wms/teams/${row.id}/members`
                        )
                      }
                    >
                      Members
                    </button>

                    <button
                      className="btn btn-sm btn-warning me-1"
                      onClick={() =>
                        navigate(
                          `/wms/teams/${row.id}/edit`
                        )
                      }
                    >
                      Edit
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        handleDelete(row)
                      }
                    >
                      Delete
                    </button>

                  </td>

                </tr>
              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}